import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { institucionalApi } from '../../api/endpoints'
import { Header } from './Header'
import { Footer } from './Footer'
import { CookieBanner } from './CookieBanner'
import { css } from './css'

const INPUT_STYLE: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: '#0B1220', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 9, padding: '12px 14px', color: '#FFFFFF', fontSize: 14, fontFamily: 'inherit',
}
const LABEL_STYLE: React.CSSProperties = { color: '#A8B0C3', fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }

export function ContatoPage() {
  const { data: config } = useQuery({ queryKey: ['configuracao-geral-publica'], queryFn: institucionalApi.configuracaoGeral, retry: false })
  const emailContato = config?.emailContato || 'contato@walleon.com.br'

  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [mensagem, setMensagem] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)

  async function enviar(e: FormEvent) {
    e.preventDefault()
    if (!nome.trim() || !email.trim() || !mensagem.trim()) {
      setErro('Preencha nome, e-mail e mensagem antes de enviar.')
      return
    }
    setErro(null)
    setEnviando(true)
    try {
      await institucionalApi.enviarContato({ nome, email, telefone: telefone || undefined, mensagem })
      setEnviado(true)
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível enviar agora. Tente de novo em instantes.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div style={css('width: 100%; min-height: 100vh; background: #0B1220; display: flex; flex-direction: column;')}>
      <Header atual="contato" />

      <div style={css('max-width: 640px; margin: 0 auto; width: 100%; box-sizing: border-box; padding: 72px 32px 120px; display: flex; flex-direction: column; gap: 8px;')}>
        <div style={css('text-align: center; display: flex; flex-direction: column; align-items: center; gap: 10px; margin-bottom: 24px;')}>
          <span style={css('color: #F5A623; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;')}>Fale conosco</span>
          <h1 style={css('margin: 0; color: #FFFFFF; font-size: 32px; font-weight: 800;')}>Como podemos ajudar?</h1>
          <p style={css('margin: 0; color: #A8B0C3; font-size: 15px; line-height: 1.7; max-width: 460px;')}>Dúvidas, sugestões ou problemas técnicos — sua mensagem chega direto pra nossa equipe, e você recebe a resposta por e-mail.</p>
        </div>

        {enviado ? (
          <div style={css('background: rgba(58,193,126,0.1); border: 1px solid rgba(58,193,126,0.3); border-radius: 14px; padding: 32px; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 12px;')}>
            <div style={css('width: 44px; height: 44px; border-radius: 999px; background: rgba(58,193,126,0.2); display: flex; align-items: center; justify-content: center; color: #3AC17E;')}>
              <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5 10 17l9-9" /></svg>
            </div>
            <div style={css('color: #FFFFFF; font-size: 17px; font-weight: 700;')}>Mensagem enviada!</div>
            <p style={css('margin: 0; color: #A8B0C3; font-size: 14px; line-height: 1.6;')}>Recebemos sua solicitação e vamos responder pelo e-mail informado assim que possível.</p>
          </div>
        ) : (
          <form onSubmit={enviar} style={css('background: #131B2C; border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 28px; display: flex; flex-direction: column; gap: 18px;')}>
            <div>
              <span style={LABEL_STYLE}>Nome</span>
              <input style={INPUT_STYLE} type="text" placeholder="Seu nome completo" value={nome} onChange={(e) => setNome(e.target.value)} required />
            </div>
            <div style={css('display: flex; gap: 14px;')}>
              <div style={{ flex: 1 }}>
                <span style={LABEL_STYLE}>E-mail</span>
                <input style={INPUT_STYLE} type="email" placeholder="voce@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div style={{ flex: 1 }}>
                <span style={LABEL_STYLE}>Telefone <span style={{ color: '#4E5872', fontWeight: 400 }}>(opcional)</span></span>
                <input style={INPUT_STYLE} type="tel" placeholder="(11) 99999-9999" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
              </div>
            </div>
            <div>
              <span style={LABEL_STYLE}>Mensagem</span>
              <textarea style={{ ...INPUT_STYLE, resize: 'vertical' }} rows={5} placeholder="Conte o que você precisa..." value={mensagem} onChange={(e) => setMensagem(e.target.value)} />
            </div>
            {erro && <p style={css('margin: 0; color: #E96A6A; font-size: 13px;')}>{erro}</p>}
            <button
              type="submit"
              disabled={enviando}
              style={css(`background: #F5A623; border: none; color: #0B1220; font-weight: 700; font-size: 15px; padding: 14px; border-radius: 9px; cursor: pointer; opacity: ${enviando ? 0.7 : 1};`)}
            >
              {enviando ? 'Enviando...' : 'Enviar mensagem'}
            </button>
          </form>
        )}

        <div style={css('text-align: center; margin-top: 8px;')}>
          <span style={css('color: #6B7691; font-size: 12.5px;')}>Prefere e-mail direto? Escreva pra <a href={`mailto:${emailContato}`} style={css('color: #F5A623; text-decoration: none;')}>{emailContato}</a></span>
        </div>
      </div>

      <Footer />
      <CookieBanner />
    </div>
  )
}
